import React, { useState, useContext } from "react";
import {
  FaBox,
  FaUsers,
  FaTasks,
  FaSun,
  FaMoon,
  FaChevronRight,
  FaChevronDown,
  FaHome,
  FaSignOutAlt,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
} from "react-icons/fa";
import logo from "../assets/infraLogo.jpg";

import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";
import { useNavigate } from "react-router-dom";

const PerOrderSidebar = ({ isMobileOpen = false, onCloseMobile = () => {} }) => {
  const { isDark, setIsDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);

  const [openMenus, setOpenMenus] = useState({});
  const [openHoverMenus, setOpenHoverMenus] = useState({});
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebar-collapsed");
    return saved === "true";
  });

  const navigate = useNavigate();

  const toggleTheme = (dark) => {
    setIsDark(dark);
    localStorage.setItem("theme-mode", dark ? "dark" : "light");

    if (dark) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  };

  // Fixed: only one main menu open at a time
  const toggleMenu = (index) => {
    setOpenMenus((prev) => {
      const isCurrentlyOpen = !!prev[index];
      return isCurrentlyOpen ? {} : { [index]: true };
    });

    // close all child popups when switching main menu
    setOpenHoverMenus({});
  };

  const handleToggleCollapse = () => {
    const newState = !collapsed;
    setCollapsed(newState);
    localStorage.setItem("sidebar-collapsed", newState);

    if (newState) {
      setOpenHoverMenus({});
    }
  };

  // Fixed: only one popup submenu open at a time
  const toggleHoverMenu = (menuKey, event) => {
    event.stopPropagation();

    setOpenHoverMenus((prev) => {
      if (prev[menuKey]) {
        return {};
      }
      return { [menuKey]: true };
    });
  };

  const closeHoverMenu = (menuKey) => {
    setOpenHoverMenus((prev) => {
      if (!prev[menuKey]) return prev;
      return {};
    });
  };

  const handleHomeClick = () => {
    window.location.href = "/temporary-home";
  };

  const handleLogout = () => {
    try {
      localStorage.removeItem("authToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("userDetails");
      localStorage.removeItem("sessionData");
      sessionStorage.clear();

      document.cookie.split(";").forEach((c) => {
        document.cookie = c
          .replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });

      window.history.pushState(null, "", window.location.href);
      window.onpopstate = function () {
        window.history.pushState(null, "", window.location.href);
      };

      window.location.replace("/login");
    } catch (error) {
      console.error("Logout error:", error);
      window.location.replace("/login");
    }
  };

  const routeMap = {
    "Estimation Type Master": "/OrderManagement/EstimationTypeMaster",
    "Work Type Master": "/OrderManagement/CategoryMaster",
    "Work Sub Type Master": "/OrderManagement/WorkInvolvedItems",
    "Major Items": "/OrderManagement/WorkInvolvedItems",
    "Tax Type": "/OrderManagement/TaxType",
    "Work Location Master": "/OrderManagement/WorkLocationMaster",
    "Work Type Entry": "/OrderManagement/WorkTypeEntry",
    "BOQ Listing": "/OrderManagement/EstimationDetailsListing_1",
    "Estimation Details Listing": "/OrderManagement/EstimationDetailsListing",
    "Project Estimation Listing": "/OrderManagement/EstimationListing",
    "Tender Details": "/OrderManagement/TenderDetailsListing",
    "BOQ Report": "/OrderManagement/EstimationDetailsReport_1",
    "Project EMD ESD FSD": "/OrderManagement/ProjectClearValues",
  };

  const navItems = [
    {
      label: "Project Master",
      icon: <FaBox />,
      submenu: [
        {
          label: "Estimation Master",
          hoverItems: [
            "Estimation Type Master",
            "Work Type Master",
            "Work Sub Type Master",
          ],
        },
        {
          label: "Major Items",
          hoverItems: ["Major Items"],
        },
        {
          label: "Tax Type Master",
          hoverItems: ["Tax Type"],
        },
        {
          label: "Work Location Master",
          hoverItems: ["Work Location Master"],
        },
        {
          label: "Work Type Master",
          hoverItems: ["Work Type Entry"],
        },
      ],
    },
    {
      label: "Project Transactions",
      icon: <FaUsers />,
      submenu: [
        {
          label: "Pre Project",
          hoverItems: [
            "BOQ Listing",
            "Estimation Details Listing",
            "Project Estimation Listing",
            "Tender Details",
          ],
        },
      ],
    },
    {
      label: "Project Reports",
      icon: <FaTasks />,
      submenu: [
        {
          label: "Pre Project Reports",
          hoverItems: ["BOQ Report"],
        },
        {
          label: "Project EMD ESD FSD Reports",
          hoverItems: ["Project EMD ESD FSD"],
        },
      ],
    },
  ];

  return (
    <>
      <div
        className={`
          ${isMobileOpen ? "fixed top-0 left-0 translate-x-0" : "-translate-x-full"}
          sm:translate-x-0 sm:sticky sm:top-0 sm:h-screen sm:flex fixed h-full
          ${collapsed ? "w-20" : "w-80"}
          z-[200] transform transition-all duration-300 flex-col text-white
          ${currentSkin.sidebar}
        `}
      >
        <div className="flex-shrink-0 px-4 py-4 border-b border-white/10 dark:border-gray-700/50">
          <div className="flex items-center justify-between">
            <button
              onClick={handleToggleCollapse}
              className="flex items-center justify-center w-10 h-10 rounded-xl bg-white/10 dark:bg-gray-700/50 backdrop-blur-sm border border-white/20 dark:border-gray-600/50 text-white hover:bg-white/20 dark:hover:bg-gray-600/50 transition-all duration-300 shadow-lg"
              title={collapsed ? "Expand Sidebar" : "Collapse Sidebar"}
            >
              {collapsed ? (
                <FaAngleDoubleRight className="text-sm" />
              ) : (
                <FaAngleDoubleLeft className="text-sm" />
              )}
            </button>

            <div className="sm:hidden">
              <button
                onClick={onCloseMobile}
                className="text-white/80 hover:text-white text-2xl font-bold transition-colors"
              >
                ×
              </button>
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 px-4 py-5 border-b border-white/10 dark:border-gray-700/50">
          <div className="flex flex-col items-center transition-all duration-300">
            <div className={`transition-all duration-300 ${collapsed ? "w-12 h-12" : "w-24 h-24"}`}>
              <img
                src={logo}
                alt="Infraconsoft Logo"
                className="w-full h-full rounded-xl object-contain shadow-lg"
                onError={(e) => {
                  e.target.src = "https://via.placeholder.com/150x150/4F46E5/FFFFFF?text=LOGO";
                }}
              />
            </div>
            {!collapsed && (
              <div className="mt-3 text-center">
                <h2 className="text-lg font-bold text-white dark:text-gray-100">Infraconsoft</h2>
                <p className="text-xs text-white/60 dark:text-gray-400"></p>
              </div>
            )}
          </div>
        </div>

        {!collapsed && (
          <div className="flex-shrink-0 px-4 py-4">
            <div className="px-4 py-3 rounded-xl bg-gradient-to-br from-blue-500/10 to-purple-500/10 dark:from-blue-600/20 dark:to-purple-600/20 backdrop-blur-sm shadow-lg border border-blue-400/20 dark:border-blue-500/30 transition-all">
              <div className="flex flex-col items-center space-y-2">
                <div className="w-8 h-8 rounded-lg bg-blue-400/20 dark:bg-blue-500/30 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4 text-blue-400 dark:text-blue-300"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                </div>
                <div className="text-center">
                  <span className="text-[10px] tracking-wider uppercase text-white/50 dark:text-gray-400 block">
                    Current Workspace
                  </span>
                  <span className="text-sm font-semibold text-white dark:text-gray-100">
                    Inventory Management
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex-1 flex flex-col min-h-0">
          <nav className="flex-1 px-4 overflow-y-auto custom-scrollbar">
            <div className="space-y-2 py-2">
              {navItems.map((item, index) => (
                <div key={index} className="relative">
                  <button
                    onClick={() => item.submenu && toggleMenu(index)}
                    className={`flex items-center justify-between px-4 py-3 rounded-xl w-full text-left transition-all duration-200 group ${
                      item.active
                        ? "bg-white/20 dark:bg-gray-700/50 shadow-lg"
                        : "hover:bg-white/10 dark:hover:bg-gray-700/30 hover:shadow-md"
                    } border border-transparent hover:border-white/10 dark:hover:border-gray-600/30`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-lg group-hover:scale-110 transition-transform duration-200">
                        {item.icon}
                      </span>
                      {!collapsed && (
                        <span className="font-medium text-white dark:text-gray-100 group-hover:text-white/90 dark:group-hover:text-gray-50">
                          {item.label}
                        </span>
                      )}
                    </div>
                    {!collapsed && item.submenu && (
                      <span className="transition-transform duration-200">
                        {openMenus[index] ? (
                          <FaChevronDown className="w-3 h-3 opacity-70" />
                        ) : (
                          <FaChevronRight className="w-3 h-3 opacity-70" />
                        )}
                      </span>
                    )}
                  </button>

                  {openMenus[index] && item.submenu && !collapsed && (
                    <div className="ml-6 mt-2 space-y-1 border-l-2 border-white/10 dark:border-gray-600/30 pl-4">
                      {item.submenu.map((sub, subIndex) => {
                        const menuKey = `${index}-${subIndex}`;

                        return (
                          <div key={subIndex} className="relative">
                            <button
                              className="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-white/10 dark:hover:bg-gray-700/30 flex items-center justify-between transition-all duration-200 border border-transparent hover:border-white/10 dark:hover:border-gray-600/30"
                              onClick={(e) => {
                                if (sub.hoverItems) {
                                  toggleHoverMenu(menuKey, e);
                                } else if (routeMap[sub.label]) {
                                  navigate(routeMap[sub.label]);
                                }
                              }}
                            >
                              <span className="text-white/80 dark:text-gray-200 hover:text-white dark:hover:text-gray-50 font-medium">
                                {sub.label}
                              </span>
                              {sub.hoverItems && (
                                <FaChevronRight
                                  className={`w-3 h-3 text-white transition-all duration-200 ${
                                    openHoverMenus[menuKey]
                                      ? "rotate-90 opacity-100"
                                      : "opacity-50 hover:opacity-70"
                                  }`}
                                />
                              )}
                            </button>

                            {sub.hoverItems && openHoverMenus[menuKey] && (
                              <div
                                className="fixed bg-white dark:bg-gray-800 backdrop-blur-md rounded-xl shadow-2xl w-72 border border-gray-200 dark:border-gray-600 overflow-hidden z-[99999] transform"
                                style={{
                                  left: `${collapsed ? 20 : 80}px`,
                                  marginLeft: "240px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  maxHeight: "80vh",
                                }}
                              >
                                <div className="py-2">
                                  <div className="px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-600 border-b border-gray-200 dark:border-gray-600 flex justify-between items-center">
                                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                                      {sub.label}
                                    </span>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        closeHoverMenu(menuKey);
                                      }}
                                      className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 text-lg leading-none w-6 h-6 flex items-center justify-center rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-all"
                                    >
                                      ×
                                    </button>
                                  </div>

                                  <div className="max-h-96 overflow-y-auto custom-scrollbar-light">
                                    {sub.hoverItems.map((hoverItem, hoverIndex) => (
                                      <div
                                        key={hoverIndex}
                                        className="px-4 py-3 cursor-pointer text-sm transition-all duration-200 border-b border-gray-100 dark:border-gray-700 last:border-b-0 group/item text-gray-900 dark:text-gray-100 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 dark:hover:from-gray-700 dark:hover:to-gray-600"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (routeMap[hoverItem]) {
                                            navigate(routeMap[hoverItem]);
                                            closeHoverMenu(menuKey);
                                          }
                                        }}
                                      >
                                        <div className="flex items-center justify-between">
                                          <span className="font-medium text-gray-900 dark:text-gray-100 group-hover/item:text-gray-900 dark:group-hover/item:text-white">
                                            {hoverItem}
                                          </span>
                                          <FaChevronRight className="w-3 h-3 text-gray-500 dark:text-gray-400 group-hover/item:text-gray-700 dark:group-hover/item:text-gray-300 opacity-0 group-hover/item:opacity-100 transition-all" />
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </nav>
        </div>

        {!collapsed && (
          <div className="flex-shrink-0 px-4 py-4 border-t border-white/10 dark:border-gray-700/50 bg-white/5 dark:bg-gray-800/20 backdrop-blur-sm">
            <div className="flex justify-center">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleTheme(false)}
                  className={`w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-200 ${
                    !isDark
                      ? "bg-white text-gray-800 shadow-lg"
                      : "bg-white/10 dark:bg-gray-700/50 text-white hover:bg-white/20 dark:hover:bg-gray-600/50"
                  }`}
                  title="Light Mode"
                >
                  <FaSun className="text-sm" />
                </button>
                <button
                  onClick={() => toggleTheme(true)}
                  className={`w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-200 ${
                    isDark
                      ? "bg-white text-gray-800 shadow-lg"
                      : "bg-white/10 dark:bg-gray-700/50 text-white hover:bg-white/20 dark:hover:bg-gray-600/50"
                  }`}
                  title="Dark Mode"
                >
                  <FaMoon className="text-sm" />
                </button>
                <button
                  onClick={handleHomeClick}
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/10 dark:bg-gray-700/50 text-white hover:bg-white/20 dark:hover:bg-gray-600/50 transition-all duration-200"
                  title="Go to Home"
                >
                  <FaHome className="text-sm" />
                </button>
                <button
                  onClick={handleLogout}
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/10 dark:bg-gray-700/50 text-white hover:bg-red-500/80 dark:hover:bg-red-600/70 transition-all duration-200"
                  title="Logout"
                >
                  <FaSignOutAlt className="text-sm" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 sm:hidden z-[150]"
          onClick={onCloseMobile}
        ></div>
      )}

      <style jsx>{`
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.1);
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 4px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.3);
          border-radius: 4px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.5);
        }

        .custom-scrollbar-light {
          scrollbar-width: thin;
          scrollbar-color: rgba(0, 0, 0, 0.3) rgba(0, 0, 0, 0.1);
        }

        .custom-scrollbar-light::-webkit-scrollbar {
          width: 6px;
        }

        .custom-scrollbar-light::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.1);
          border-radius: 3px;
        }

        .custom-scrollbar-light::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.3);
          border-radius: 3px;
        }

        .custom-scrollbar-light::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 0, 0, 0.5);
        }

        .custom-scrollbar,
        .custom-scrollbar-light {
          scroll-behavior: smooth;
        }

        .dark .custom-scrollbar {
          scrollbar-color: rgba(156, 163, 175, 0.5) rgba(75, 85, 99, 0.3);
        }

        .dark .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(75, 85, 99, 0.3);
        }

        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(156, 163, 175, 0.5);
        }

        .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(156, 163, 175, 0.7);
        }
      `}</style>
    </>
  );
};

export default PerOrderSidebar;